import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';
import { examplePresets, ExamplePreset } from './examplePresets';

export const MAX_GHOST_MANNEQUIN_IMAGES = 20;

interface ImageUploadProps {
  imageUrls: string[];
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
  isExample?: boolean;
  onExampleSelect?: (presetId: number) => void;
  onUserUpload?: () => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrls,
  setImageUrls,
  isExample = false,
  onExampleSelect,
  onUserUpload,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();

  const uploadPercent =
    uploadProgress && uploadProgress.total > 0
      ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
      : 0;

  const parseUploadedUrls = (data: {
    success?: boolean;
    uploaded?: { url: string }[];
    urls?: string[];
    url?: string;
  }): string[] => {
    if (!data.success) return [];
    if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
      return data.uploaded.map((item) => item.url);
    }
    if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
      return data.urls;
    }
    if (data.url) {
      return [data.url];
    }
    return [];
  };

  const uploadSingleFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('userId', currentUser?.uid || 'anonymous');

    const response = await fetch(API_CONFIG.AI_GHOST_MANNEQUIN_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const urls = parseUploadedUrls(data);
    return urls[0] ?? null;
  };

  const handleFiles = async (files: FileList | null, append: boolean) => {
    if (!files || files.length === 0) return;

    const slotsLeft = append ? MAX_GHOST_MANNEQUIN_IMAGES - imageUrls.length : MAX_GHOST_MANNEQUIN_IMAGES;
    if (slotsLeft <= 0) {
      setUploadStatus(`Maximum ${MAX_GHOST_MANNEQUIN_IMAGES} images allowed.`);
      return;
    }

    const selected = Array.from(files).slice(0, slotsLeft);
    if (selected.length < files.length) {
      setUploadStatus(`Only ${selected.length} file(s) added (max ${MAX_GHOST_MANNEQUIN_IMAGES} images).`);
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: selected.length });
    if (selected.length === files.length || !append) {
      setUploadStatus(null);
    }

    const baseUrls = append ? imageUrls : [];
    const newUrls: string[] = [];

    try {
      for (let i = 0; i < selected.length; i++) {
        const url = await uploadSingleFile(selected[i]);
        if (!url) {
          if (newUrls.length === 0) {
            setUploadStatus('Upload failed. Please try again.');
            if (!append) setImageUrls([]);
          } else {
            setUploadStatus(
              `Uploaded ${newUrls.length} of ${selected.length} image(s). Some files failed — try again for the rest.`
            );
            onUserUpload?.();
            setImageUrls([...baseUrls, ...newUrls].slice(0, MAX_GHOST_MANNEQUIN_IMAGES));
          }
          return;
        }
        newUrls.push(url);
        setUploadProgress({ done: i + 1, total: selected.length });
      }

      if (newUrls.length > 0) {
        onUserUpload?.();
        const merged = [...baseUrls, ...newUrls].slice(0, MAX_GHOST_MANNEQUIN_IMAGES);
        setImageUrls(merged);
        setUploadStatus(`Upload successful! ${merged.length} image(s) ready.`);
      }
    } catch {
      setUploadStatus('Upload failed. Please try again.');
      if (!append && newUrls.length === 0) setImageUrls([]);
      else if (newUrls.length > 0) {
        onUserUpload?.();
        setImageUrls([...baseUrls, ...newUrls].slice(0, MAX_GHOST_MANNEQUIN_IMAGES));
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const renderUploadProgress = () => {
    if (!uploading || !uploadProgress) return null;

    return (
      <div className="w-full max-w-sm mt-4">
        <div className="flex justify-between text-xs text-text-description mb-1">
          <span>
            Uploading {uploadProgress.done} of {uploadProgress.total}
          </span>
          <span>{uploadPercent}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-secondary-500 transition-all duration-300 ease-out"
            style={{ width: `${uploadPercent}%` }}
          />
        </div>
      </div>
    );
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const append = imageUrls.length > 0 && !isExample;
      handleFiles(e.dataTransfer.files, append);
    },
    [imageUrls.length, isExample]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const openFilePicker = (append: boolean) => {
    fileInputRef.current?.setAttribute('data-append', append ? 'true' : 'false');
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const append = e.target.getAttribute('data-append') === 'true';
    handleFiles(e.target.files, append);
  };

  const handleExampleClick = (presetId: number) => {
    if (imageUrls.length > 0) return;
    onExampleSelect?.(presetId);
  };

  const removeImageAt = (index: number) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
    setUploadStatus(null);
  };

  const hasUploadedImages = imageUrls.length > 0;
  const canAddMore = !isExample && imageUrls.length > 0 && imageUrls.length < MAX_GHOST_MANNEQUIN_IMAGES;

  return (
    <div className="bg-accent-sand rounded-lg p-4">
      <div className="space-y-4">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="image/*"
          multiple
        />

        {hasUploadedImages ? (
          <div
            className="bg-white rounded-lg p-4 border-2 border-dashed border-accent-taupe relative"
            onDrop={canAddMore ? handleDrop : undefined}
            onDragOver={canAddMore ? handleDragOver : undefined}
          >
            <div className="flex items-center justify-between mb-3 pr-8">
              <h3 className="font-medium text-text-heading">
                {isExample ? 'Example Garment Image' : `Garment Images (${imageUrls.length}/${MAX_GHOST_MANNEQUIN_IMAGES})`}
              </h3>
              <button
                className="absolute top-3 right-3 text-text-description hover:text-red-500 text-2xl focus:outline-none"
                onClick={() => {
                  setImageUrls([]);
                  setUploadStatus(null);
                }}
                aria-label="Clear all images"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
              {imageUrls.map((url, idx) => (
                <div key={`${url}-${idx}`} className="relative bg-accent-sand rounded border border-accent-taupe aspect-square">
                  <img src={url} alt={`Garment ${idx + 1}`} className="w-full h-full object-contain p-1" />
                  {!isExample && (
                    <button
                      type="button"
                      onClick={() => removeImageAt(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-red-600 text-sm hover:bg-red-50 shadow"
                      aria-label={`Remove image ${idx + 1}`}
                    >
                      &times;
                    </button>
                  )}
                  <span className="absolute bottom-1 left-1 bg-secondary-500 text-white text-[10px] px-1 rounded">
                    {idx + 1}
                  </span>
                </div>
              ))}
            </div>

            {canAddMore && (
              <button
                type="button"
                onClick={() => openFilePicker(true)}
                disabled={uploading}
                className="mt-3 w-full py-2 text-sm border border-secondary-500 text-secondary-600 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
              >
                <i className="fas fa-plus mr-2"></i>
                Add more images ({MAX_GHOST_MANNEQUIN_IMAGES - imageUrls.length} slots left)
              </button>
            )}

            {renderUploadProgress()}

            {!isExample && uploadStatus && !uploading && (
              <p
                className={`mt-3 text-sm ${
                  uploadStatus.includes('failed') || uploadStatus.includes('Maximum') ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {uploadStatus}
              </p>
            )}
            {isExample && (
              <p className="text-text-description mt-3 text-xs">
                This is an example image. Upload your own garment photos to generate ghost mannequin outputs.
              </p>
            )}
          </div>
        ) : (
          <div
            className="bg-accent-sand rounded-lg p-6 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center min-h-[320px]"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-cloud-upload-alt text-secondary-600 text-2xl"></i>
            </div>
            <h3 className="font-medium text-text-heading mb-2">Upload Garment Images</h3>
            <p className="text-sm text-text-description mb-4 text-center px-2">
              Drag & drop up to {MAX_GHOST_MANNEQUIN_IMAGES} images, or click to browse (bulk upload supported).
              <span className="block mt-1 text-xs">
                Over 4000px images are scaled to 4000px to run efficiently.
              </span>
            </p>
            {uploading ? (
              <div className="flex flex-col items-center w-full px-4">
                <span className="inline-block w-8 h-8 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin mb-2"></span>
                {renderUploadProgress()}
              </div>
            ) : (
              <button
                className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-2 rounded-lg font-medium"
                onClick={() => openFilePicker(false)}
                disabled={uploading}
              >
                Select Files
              </button>
            )}
            <p className="text-xs text-text-description mt-3">JPG, PNG, WebP up to 10MB each</p>
            {uploadStatus && !uploading && (
              <p className={`mt-2 text-sm ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>
                {uploadStatus}
              </p>
            )}
          </div>
        )}

        <div className="mt-6">
          <h4 className={`text-sm font-medium mb-3 ${hasUploadedImages ? 'text-text-description' : 'text-text-heading'}`}>
            Or try an example:
          </h4>
          <div className="grid grid-cols-3 gap-3">
            {examplePresets.map((preset: ExamplePreset) => (
              <button
                key={preset.id}
                onClick={() => handleExampleClick(preset.id)}
                disabled={hasUploadedImages}
                className={`group relative rounded-lg overflow-hidden ${
                  hasUploadedImages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <img
                  src={preset.preview}
                  alt={preset.name}
                  className={`w-full h-32 object-contain transition-opacity ${
                    hasUploadedImages ? 'opacity-50 grayscale' : 'group-hover:opacity-75'
                  }`}
                />
                <p
                  className={`absolute bottom-1 left-1 right-1 text-xs text-center rounded px-1 py-0.5 ${
                    hasUploadedImages
                      ? 'text-text-description bg-accent-sand0 bg-opacity-50'
                      : 'text-white bg-black bg-opacity-50'
                  }`}
                >
                  {preset.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
